// Display formatting for the console (ui-spec §4.10). Money arrives as a `MoneyUSD` decimal string and is handled
// as integer micros so no float rounding ever changes a displayed amount.

const MICROS = 1_000_000;

/**
 * Parses a decimal money string ("3.12", "-0.5", "40", "0.0000345", "1.2e-5") into integer micros, half-up past the
 * sixth decimal; undefined when malformed or too large to hold exactly.
 */
export function parseMicros(value: string | null | undefined): number | undefined {
  if (value == null) return undefined;
  const m = /^([+-])?(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(value.trim());
  if (!m) return undefined;
  const digits = m[2]! + (m[3] ?? "");
  // Where the micros' units digit ends inside `digits`, after the exponent moves the decimal point.
  const point = m[2]!.length + Number(m[4] ?? 0) + 6;
  if (point > 22) return undefined;
  const whole = point <= 0 ? 0 : Number(digits.slice(0, point).padEnd(point, "0"));
  const next = point < 0 ? "0" : (digits[point] ?? "0");
  const micros = whole + (next >= "5" ? 1 : 0);
  if (!Number.isSafeInteger(micros)) return undefined;
  return m[1] === "-" && micros !== 0 ? -micros : micros;
}

const formats = new Map<string, { digits: number; format: (n: number) => string }>();

/** The currency's own formatter and fraction digits (USD 2, JPY 0); an unknown code is shown before the amount. */
function currencyFormat(currency: string) {
  const code = currency.trim().toUpperCase() || "USD";
  let f = formats.get(code);
  if (!f) {
    try {
      const nf = new Intl.NumberFormat("en-US", { style: "currency", currency: code });
      f = { digits: Math.min(nf.resolvedOptions().maximumFractionDigits ?? 2, 6), format: (n) => nf.format(n) };
    } catch {
      const nf = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      f = { digits: 2, format: (n) => `${code} ${nf.format(n)}` };
    }
    formats.set(code, f);
  }
  return f;
}

/**
 * `$3.12`, `€310.20`, `¥1,284`: the currency's own symbol and fraction digits, half-up on integer micros. A nonzero
 * amount below the currency's smallest unit, either sign, reads `< $0.01`, never `$0.00`. An empty
 * currency code is USD, the platform's money.
 */
export function formatMoney(value: string | number | null | undefined, currency = "USD"): string {
  const micros = typeof value === "number" ? (Number.isFinite(value) ? Math.round(value * MICROS) : undefined) : parseMicros(value ?? undefined);
  if (micros === undefined) return "—";
  const f = currencyFormat(currency);
  const unit = 10 ** (6 - f.digits);
  const abs = Math.abs(micros);
  if (abs > 0 && abs < unit) return `< ${f.format(unit / MICROS)}`;
  const units = Math.floor((abs + unit / 2) / unit);
  return f.format(((micros < 0 ? -1 : 1) * units * unit) / MICROS);
}

/** `$2.8457/h`: rates keep up to four decimals (at least two), trailing zeros trimmed past the second. */
export function formatRate(value: string | null | undefined, unit = "h"): string {
  const micros = parseMicros(value);
  if (micros === undefined) return "—";
  const tenThousandths = Math.round(micros / 100);
  const dollars = Math.floor(tenThousandths / 10_000).toLocaleString("en-US");
  let frac = String(tenThousandths % 10_000).padStart(4, "0");
  while (frac.length > 2 && frac.endsWith("0")) frac = frac.slice(0, -1);
  return `$${dollars}.${frac}/${unit}`;
}

/** Largest two units: `1h 12m`, `42s`, `3d 4h`. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const units: [number, string][] = [
    [Math.floor(s / 86_400), "d"],
    [Math.floor((s % 86_400) / 3_600), "h"],
    [Math.floor((s % 3_600) / 60), "m"],
    [s % 60, "s"],
  ];
  const first = units.findIndex(([n]) => n > 0);
  if (first === -1) return "0s";
  const parts = units.slice(first, first + 2).filter(([n]) => n > 0);
  return parts.map(([n, u]) => `${n}${u}`).join(" ");
}

/** "just now" under 10 s, then `18m ago`, `2h ago`, `Yesterday`, then a date. */
export function formatRelative(time: string | Date, now: Date, timeZone = "UTC"): string {
  const t = typeof time === "string" ? new Date(time) : time;
  const diff = now.getTime() - t.getTime();
  if (Number.isNaN(diff)) return "—";
  if (diff < 0) return formatAbsolute(t, timeZone);
  const s = diff / 1000;
  if (s < 10) return "just now";
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3_600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86_400) return `${Math.floor(s / 3_600)}h ago`;
  if (s < 2 * 86_400) return "Yesterday";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone }).format(t);
}

/** `Sep 30 13:12:04`: the compact log timestamp in the user's timezone (the zone is in the tooltip). */
export function formatLogTime(time: string | Date, timeZone = "UTC"): string {
  const t = typeof time === "string" ? new Date(time) : time;
  if (Number.isNaN(t.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone,
  }).formatToParts(t);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("month")} ${get("day")} ${hour}:${get("minute")}:${get("second")}`;
}

/** `2026-09-30 13:12:04 UTC` in the user's timezone (UTC by default, IMP-15). */
export function formatAbsolute(time: string | Date, timeZone = "UTC"): string {
  const t = typeof time === "string" ? new Date(time) : time;
  if (Number.isNaN(t.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone,
    timeZoneName: "short",
  }).formatToParts(t);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")} ${hour}:${get("minute")}:${get("second")} ${get("timeZoneName")}`;
}

/** `13:12 UTC`. */
export function formatClock(time: string | Date, timeZone = "UTC"): string {
  const t = typeof time === "string" ? new Date(time) : time;
  if (Number.isNaN(t.getTime())) return "—";
  const hm = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone }).format(t);
  return `${hm} ${timeZone === "UTC" ? "UTC" : timeZone}`;
}

/** Stored bytes in IEC units with one decimal: `141.9 MiB`, `912 B` (IMP-19). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  const units = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return i === 0 ? `${v} B` : `${v.toFixed(1)} ${units[i]}`;
}

/** Thousands separators: `4,000`. */
export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

/** P7: exact once fully paged, otherwise a floor ("500+"). */
export function formatCountFloor(n: number, complete: boolean): string {
  return complete ? formatCount(n) : `${formatCount(n)}+`;
}

/** Middle truncation for long names: `lora-fine-tune…-v3`, the full name goes in a tooltip. */
export function truncateMiddle(value: string, max = 32): string {
  if (value.length <= max) return value;
  const keep = max - 1;
  const head = Math.ceil(keep * 0.6);
  return `${value.slice(0, head)}…${value.slice(value.length - (keep - head))}`;
}
